import Router from "next/router";
import React from "react";

import { getPrice, truncateTo2Decimals } from "../../services/frontend/pricing.service";
import { getStockAvailability } from "../../services/helper";
import { trackEvent } from "../../utils/analytics";
import { EVENTS } from "../../utils/analyticsConfig";
import LoaderComponent from "../Loader";
import CartIconSvg from "../Navbar/CartIconSvg";
import BagItem from "./BagItem";

export default class Basket extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      refresh: false,
      pageHeight: 0,
      newBottom: false,
      isStockAvailable: false,
    };
  }

  componentDidMount() {
    this._isBasketMounted = true;
    this.setState({ pageHeight: window.innerHeight });
    window.addEventListener("resize", this.handleWindowResize, true);
    window.visualViewport?.addEventListener("resize", this.handleWindowResize);
    window.visualViewport?.addEventListener("scroll", this.handleWindowResize);
    this.handleWindowResize();
    getStockAvailability(this.props.cartData.products).then((isStockAvailable) => {
      if (this._isBasketMounted) {
        this.setState({ isStockAvailable });
      }
    });
  }

  componentWillUnmount() {
    this._isBasketMounted = false;
    if (this.removeTimer) {
      clearTimeout(this.removeTimer);
    }
    window.removeEventListener("resize", this.handleWindowResize, true);
    window.visualViewport?.removeEventListener("resize", this.handleWindowResize);
    window.visualViewport?.removeEventListener("scroll", this.handleWindowResize);
  }

  handleWindowResize = () => {
    const height = window.visualViewport?.height ?? window.innerHeight;
    document.documentElement.style.setProperty("--vh", `${height * 0.01}px`);
  };

  removeProductFromCart = (productId) => () => {
    const cartData = this.props.cartData;
    const filteredProducts = cartData.products.filter((product) => product.id !== productId);
    if (filteredProducts.length === 0) {
      if (!this.props.fromProductPage) this.props.setRefresh();
    }

    const timerId = setTimeout(() => {
      cartData.products = filteredProducts;

      if (this.props.fromProductPage) {
        this.props.setCart(cartData, true);
      } else {
        this.props.setCart(cartData);
      }

      this.setState({ refresh: !this.state.refresh });
      if (this.props.updateAddedToCart) {
        this.props.updateAddedToCart(productId, false);
      }
    }, 300);
    this.removeTimer = timerId;
  };

  updateProductCount = (productId, variant, count) => {
    const cartData = this.props.cartData;
    const productIndex = cartData.products.findIndex(
      (product) => product.id === productId && product.size === variant,
    );
    if (productIndex < 0) return;

    cartData.products[productIndex].quantity = count;
    this.props.setCart(cartData);
    this.setState({ refresh: !this.state.refresh });
  };

  render() {
    const { productTotalPrice } = getPrice({ shippingCharge: 0 }, this.props.cartData, null, false);
    const price = truncateTo2Decimals(productTotalPrice);

    const isEmpty = !this.props.cartData.products.length;
    const isCheckoutDisabled = Boolean(this.props.store?.checkout) || isEmpty;

    return (
      <aside
        className={`fixed left-1/2 top-0 z-[101] m-0 flex h-screen w-full max-w-app flex-col overflow-hidden bg-background font-montserrat text-fontBackground transition-transform duration-500 ease-in-out ${
          this.props.isBasketOpen
            ? "-translate-x-1/2 opacity-100"
            : "pointer-events-none translate-x-[50vw] opacity-0"
        }`}
        style={{ height: "calc(var(--vh, 1vh) * 100)" }}
      >
        {this.state.loading && <LoaderComponent />}

        <div className="relative z-10 shrink-0 bg-background">
          <h2 className="relative mx-5 mt-8 flex items-center text-[20px] font-semibold text-checkoutText">
            <span>{this.props.store?.name}</span>

            <button
              className="absolute right-0 z-50 -mr-3 flex h-[38px] w-[38px] items-center justify-center border-none bg-transparent"
              onClick={() => this.props.handleShowCart(!this.props.isBasketOpen)}
              aria-label="Close cart"
            >
              <CartIconSvg filled={!isEmpty} />
            </button>
          </h2>

          <div
            className="via-background/40 pointer-events-none absolute inset-x-0 top-full h-8 bg-gradient-to-b from-background from-[20%] to-transparent"
            aria-hidden="true"
          />
        </div>

        <div className="hide-scrollbar min-h-0 flex-1 overflow-y-auto px-5 pb-24">
          {isEmpty ? (
            <p className="mt-6 text-[16px] font-normal text-checkoutText">
              Your cart seems to be empty.
            </p>
          ) : (
            <div className="mt-6 flex w-full flex-col gap-y-5">
              {this.props.cartData.products.map((product) => (
                <BagItem
                  key={`${product.id}-${product.size || ""}`}
                  item={product}
                  removeProductFromCart={this.removeProductFromCart}
                  updateProductCount={this.updateProductCount}
                />
              ))}
            </div>
          )}
        </div>

        <div className="relative shrink-0">
          <div
            className="via-background/40 pointer-events-none absolute inset-x-0 bottom-full h-24 bg-gradient-to-t from-background from-[20%] to-transparent"
            aria-hidden="true"
          />

          <button
            className={`flex w-full items-center border-none bg-[var(--theme-action-fill)] px-5 py-6 text-[13px] font-semibold text-[var(--theme-action-text)] outline-none ${
              isEmpty ? "justify-center" : "justify-between"
            }`}
            style={{
              opacity: isEmpty ? 1 : isCheckoutDisabled ? 0.2 : 1,
              paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom, 0px))",
            }}
            disabled={!isEmpty && isCheckoutDisabled}
            onClick={async () => {
              if (isEmpty) {
                this.props.handleShowCart(false);
                return;
              }

              const productsWithoutStore = this.props.cartData.products.map(
                ({ store, ...productWithoutStore }) => productWithoutStore,
              );
              trackEvent(EVENTS.CHECKOUT_STARTED, {
                subdomain: this.props?.store?.username,
                cart_data: productsWithoutStore,
              });

              Router.push("/checkout");
            }}
          >
            {isEmpty ? (
              <span>Continue Shopping</span>
            ) : (
              <>
                <span>Proceed to Checkout</span>
                <span>₹{price}</span>
              </>
            )}
          </button>
        </div>
      </aside>
    );
  }
}
